import React, { useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import { educationAPI } from "../api/axiosClient";
import { ForgotPasswordRequest } from "../api/types";
import { ArrowLeft, Phone } from "lucide-react";
import OTPVerificationForm from "./OTPVerificationForm";
import ResetPasswordForm from "./ResetPasswordForm";
import { IMaskInput } from "react-imask";
import { SimpleDropdown } from "./ui/simple-dropdown";

interface ForgotPasswordFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBack,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => {
    setError(null);
  };

  const [formData, setFormData] = useState<ForgotPasswordRequest>({
    email: "",
    phoneNumber: "",
    phoneCode: "90",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showOTP, setShowOTP] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    id: string | number;
    label: string;
    value: string | number;
  } | null>({ id: "90", label: "🇹🇷 +90", value: "90" });

  const countryCodes = [
    { code: "90", name: "Türkiye (+90)", flag: "🇹🇷" },
    { code: "1", name: "USA (+1)", flag: "🇺🇸" },
    { code: "44", name: "UK (+44)", flag: "🇬🇧" },
    { code: "49", name: "Germany (+49)", flag: "🇩🇪" },
    { code: "33", name: "France (+33)", flag: "🇫🇷" },
    { code: "39", name: "Italy (+39)", flag: "🇮🇹" },
    { code: "34", name: "Spain (+34)", flag: "🇪🇸" },
    { code: "31", name: "Netherlands (+31)", flag: "🇳🇱" },
    { code: "32", name: "Belgium (+32)", flag: "🇧🇪" },
    { code: "46", name: "Sweden (+46)", flag: "🇸🇪" },
    { code: "47", name: "Norway (+47)", flag: "🇳🇴" },
    { code: "45", name: "Denmark (+45)", flag: "🇩🇰" },
    { code: "358", name: "Finland (+358)", flag: "🇫🇮" },
    { code: "7", name: "Russia (+7)", flag: "🇷🇺" },
    { code: "86", name: "China (+86)", flag: "🇨🇳" },
    { code: "81", name: "Japan (+81)", flag: "🇯🇵" },
    { code: "82", name: "South Korea (+82)", flag: "🇰🇷" },
    { code: "91", name: "India (+91)", flag: "🇮🇳" },
    { code: "971", name: "UAE (+971)", flag: "🇦🇪" },
    { code: "966", name: "Saudi Arabia (+966)", flag: "🇸🇦" },
    { code: "20", name: "Egypt (+20)", flag: "🇪🇬" },
    { code: "27", name: "South Africa (+27)", flag: "🇿🇦" },
    { code: "55", name: "Brazil (+55)", flag: "🇧🇷" },
    { code: "54", name: "Argentina (+54)", flag: "🇦🇷" },
    { code: "52", name: "Mexico (+52)", flag: "🇲🇽" },
    { code: "61", name: "Australia (+61)", flag: "🇦🇺" },
    { code: "64", name: "New Zealand (+64)", flag: "🇳🇿" },
  ];

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Telefon numarası gereklidir";
    } else {
      // IMaskInput'tan gelen değer mask'lanmış olabilir, sadece rakamları al
      const phoneNumber = formData.phoneNumber.replace(/\D/g, "");

      // Ülke koduna göre farklı validation kuralları
      if (formData.phoneCode === "90") {
        // Türkiye: 10 haneli (5xx xxx xx xx)
        if (phoneNumber.length !== 10) {
          newErrors.phoneNumber =
            "Türkiye telefon numarası 10 haneli olmalıdır";
        } else if (!phoneNumber.startsWith("5")) {
          newErrors.phoneNumber = "Türkiye telefon numarası 5 ile başlamalıdır";
        }
      } else if (formData.phoneCode === "1") {
        // USA/Canada: 10 haneli
        if (phoneNumber.length !== 10) {
          newErrors.phoneNumber = "Telefon numarası 10 haneli olmalıdır";
        }
      } else if (formData.phoneCode === "44") {
        // UK: 10-11 haneli
        if (phoneNumber.length < 10 || phoneNumber.length > 11) {
          newErrors.phoneNumber = "UK telefon numarası 10-11 haneli olmalıdır";
        }
      } else {
        // Diğer ülkeler için genel validation
        if (phoneNumber.length < 6 || phoneNumber.length > 15) {
          newErrors.phoneNumber = "Geçerli bir telefon numarası girin";
        }
      }
    }
    if (!formData.phoneCode?.trim()) {
      newErrors.phoneCode = "Ülke kodu gereklidir";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      console.log("Forgot password attempt (Phone):", {
        phoneCode: formData.phoneCode,
        phoneNumber: formData.phoneNumber,
      });

      const request: ForgotPasswordRequest = {
        email: undefined,
        phoneNumber: formData.phoneNumber,
        phoneCode: formData.phoneCode,
      };

      setLoading(true);
      setError(null);
      const response = await educationAPI.forgotPassword(request);

      if (response?.success) {
        // Phone flow - show OTP form
        setShowOTP(true);
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError(err instanceof Error ? err.message : "Forgot password failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPBack = () => {
    setShowOTP(false);
    clearError();
  };

  const handleOTPSuccess = (
    phoneCode: string,
    phoneNumber: string,
    otpCode: string,
  ) => {
    // Store OTP code from the OTP form
    setOtpCode(otpCode);
    setShowOTP(false);
    setShowResetPassword(true);
  };

  const handleResetPasswordBack = () => {
    setShowResetPassword(false);
    setShowOTP(true);
  };

  const handleResetPasswordSuccess = () => {
    // Password reset successful, go back to login
    onSuccess();
  };

  // Show OTP Verification Form
  if (showOTP) {
    return (
      <OTPVerificationForm
        onBack={handleOTPBack}
        onSuccess={handleOTPSuccess}
        phoneCode={formData.phoneCode || ""}
        phoneNumber={formData.phoneNumber || ""}
        email={formData.email || undefined}
      />
    );
  }

  // Show Reset Password Form
  if (showResetPassword) {
    return (
      <ResetPasswordForm
        onBack={handleResetPasswordBack}
        onSuccess={handleResetPasswordSuccess}
        phoneCode={formData.phoneCode || ""}
        phoneNumber={formData.phoneNumber || ""}
        otpCode={otpCode}
        email={formData.email || undefined}
      />
    );
  }

  // Dynamic phone mask based on country code
  const getPhoneMask = (countryCode: string): string => {
    switch (countryCode) {
      case "90": // Türkiye
        return "(000) 000-0000";
      case "1": // USA/Canada
        return "(000) 000-0000";
      case "44": // UK
        return "0000 000000";
      case "49": // Germany
        return "000 00000000";
      case "33": // France
        return "0 00 00 00 00";
      case "39": // Italy
        return "000 000 0000";
      case "34": // Spain
        return "000 000 000";
      case "31": // Netherlands
        return "0 00000000";
      case "32": // Belgium
        return "000 000 000";
      case "46": // Sweden
        return "000 000 000";
      case "47": // Norway
        return "000 00 000";
      case "45": // Denmark
        return "00 00 00 00";
      case "358": // Finland
        return "000 000 0000";
      case "7": // Russia
        return "(000) 000-00-00";
      case "86": // China
        return "000 0000 0000";
      case "81": // Japan
        return "00 0000 0000";
      case "82": // South Korea
        return "000 0000 0000";
      case "91": // India
        return "00000 00000";
      case "971": // UAE
        return "000 000 0000";
      case "966": // Saudi Arabia
        return "000 000 0000";
      case "20": // Egypt
        return "000 000 0000";
      case "27": // South Africa
        return "000 000 0000";
      case "55": // Brazil
        return "(00) 00000-0000";
      case "54": // Argentina
        return "000 000-0000";
      case "52": // Mexico
        return "000 000 0000";
      case "61": // Australia
        return "000 000 000";
      case "64": // New Zealand
        return "000 000 0000";
      default:
        return "000000000000";
    }
  };

  // Get placeholder based on country code
  const getPhonePlaceholder = (countryCode: string): string => {
    switch (countryCode) {
      case "90":
        return "(5XX) XXX-XXXX";
      case "1":
        return "(XXX) XXX-XXXX";
      case "44":
        return "XXXX XXXXXX";
      case "49":
        return "XXX XXXXXXXX";
      case "33":
        return "X XX XX XX XX";
      case "39":
        return "XXX XXX XXXX";
      case "34":
        return "XXX XXX XXX";
      case "31":
        return "X XXXXXXXX";
      case "32":
        return "XXX XXX XXX";
      case "46":
        return "XXX XXX XXX";
      case "47":
        return "XXX XX XXX";
      case "45":
        return "XX XX XX XX";
      case "358":
        return "XXX XXX XXXX";
      case "7":
        return "(XXX) XXX-XX-XX";
      case "86":
        return "XXX XXXX XXXX";
      case "81":
        return "XX XXXX XXXX";
      case "82":
        return "XXX XXXX XXXX";
      case "91":
        return "XXXXX XXXXX";
      case "971":
        return "XXX XXX XXXX";
      case "966":
        return "XXX XXX XXXX";
      case "20":
        return "XXX XXX XXXX";
      case "27":
        return "XXX XXX XXXX";
      case "55":
        return "(XX) XXXXX-XXXX";
      case "54":
        return "XXX XXX-XXXX";
      case "52":
        return "XXX XXX XXXX";
      case "61":
        return "XXX XXX XXX";
      case "64":
        return "XXX XXX XXXX";
      default:
        return "XXXXXXXXXXXX";
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Sol Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-4">Şifre Sıfırlama</h1>
            <p className="text-lg text-white/80">
              Şifrenizi unuttuysanız endişelenmeyin, hemen sıfırlayabilirsiniz
            </p>
          </div>

          <div className="space-y-4 mt-8">
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </div>
              <span>SMS ile doğrulama kodu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sağ Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo.png"
              alt="EduPayz Logo"
              className="h-16 w-auto object-contain mx-auto mb-4"
            />
          </div>

          <div className="mb-4 flex flex-col items-center">
            <div className="mb-6">
              <img
                src="/logo.png"
                alt="EduPayz Logo"
                className="h-20 w-auto object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              Şifremi Unuttum
            </h2>
            <p className="text-gray-500 mt-2">
              Şifrenizi sıfırlamak için telefon numaranızı girin
            </p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {error}
                  </div>
                )}

                {/* Phone Input */}
                <div className="space-y-2">
                  <Label
                    htmlFor="phoneNumber"
                    className="text-sm font-medium text-gray-700"
                  >
                    Telefon Numarası
                  </Label>
                  <div className="flex space-x-2">
                    <SimpleDropdown
                      options={countryCodes.map((code) => ({
                        id: code.code,
                        label: `${code.flag} +${code.code}`,
                        value: code.code,
                      }))}
                      value={selectedCountryCode}
                      onChange={(countryCode) => {
                        if (countryCode) {
                          setSelectedCountryCode(countryCode);
                          setFormData((prev) => ({
                            ...prev,
                            phoneCode: String(countryCode.value),
                            phoneNumber: "",
                          }));
                          if (errors.phoneCode) {
                            setErrors((prev) => ({ ...prev, phoneCode: "" }));
                          }
                          if (errors.phoneNumber) {
                            setErrors((prev) => ({
                              ...prev,
                              phoneNumber: "",
                            }));
                          }
                        }
                      }}
                      placeholder="Ülke"
                      disabled={loading}
                      className="w-28"
                    />
                    <IMaskInput
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      mask={getPhoneMask(formData.phoneCode || "90")}
                      placeholder={getPhonePlaceholder(
                        formData.phoneCode || "90",
                      )}
                      value={formData.phoneNumber || ""}
                      onAccept={(value: string) => {
                        setFormData((prev) => ({
                          ...prev,
                          phoneNumber: value,
                        }));
                        if (errors.phoneNumber) {
                          setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                        }
                      }}
                      className="flex-1 text-left text-sm font-mono px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0] transition-all hover:border-gray-300 bg-white"
                      inputMode="numeric"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Telefon numaranıza SMS ile doğrulama kodu gönderilecektir.
                  </p>
                  {errors.phoneNumber && (
                    <p className="text-xs text-red-500">{errors.phoneNumber}</p>
                  )}
                  {errors.phoneCode && (
                    <p className="text-xs text-red-500">{errors.phoneCode}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0] text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-[#9B51E0]/25"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Gönderiliyor...
                    </span>
                  ) : (
                    "SMS Doğrulama Kodu Gönder"
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="flex items-center justify-center space-x-2 text-gray-500 hover:text-[#9B51E0] font-medium transition-colors mx-auto"
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Giriş sayfasına dön</span>
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 EduPayz. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
